import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { searchGoogleSheetsLibrary, getRandomLibrarySample, GoogleSheetsBook } from '@/services/libraryClient';
import { SearchFilters } from '@/types/library';
import { parseQueryParams, buildQueryParams } from '@/utils/catalogueQueryState';
import Breadcrumbs from '@/components/library/Breadcrumbs';
import LibrarySearchBar from '@/components/library/LibrarySearchBar';
import LibraryFilters from '@/components/library/LibraryFilters';
import ResultDensityControls from '@/components/library/ResultDensityControls';
import BookCard from '@/components/library/BookCard';
import BookRow from '@/components/library/BookRow';
import Pagination from '@/components/library/Pagination';
import BookDetailModal from '@/components/library/BookDetailModal';
import { BookOpen, AlertCircle, ExternalLink } from 'lucide-react';

type ViewMode = 'grid' | 'list';

// Enhanced Book Card for Google Sheets data
const EnhancedBookCard: React.FC<{ book: GoogleSheetsBook; onClick: () => void }> = ({ book, onClick }) => {
  const getInitials = (title?: string) => {
    if (!title) return 'BK';
    const words = title.split(' ').filter(w => w.length > 2);
    return words.slice(0, 2).map(w => w[0].toUpperCase()).join('');
  };

  return (
    <div 
      className="bg-white rounded-lg border border-gray-200 hover:border-green-500 hover:shadow-xl transition-all duration-200 h-full flex flex-col overflow-hidden cursor-pointer"
      onClick={onClick}
    >
      {/* Cover Image */}
      <div className="relative bg-gray-100 aspect-[2/3] overflow-hidden">
        {book.coverUrl ? (
          <img
            src={book.coverUrl}
            alt={`Cover of ${book.title}`}
            loading="lazy"
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-green-100 to-green-50">
            <BookOpen className="w-16 h-16 text-green-600 opacity-40" />
          </div>
        )}
      </div>

      {/* Book Info */}
      <div className="p-4 flex-1 flex flex-col">
        <h3 className="font-bold text-gray-900 mb-2 line-clamp-2 text-lg">
          {book.title || 'Untitled'}
        </h3>
        
        <div className="space-y-1 text-sm mb-3">
          {book.author && (
            <p className="text-gray-700">
              <span className="font-medium">Author:</span> {book.author}
            </p>
          )}
          {book.publicationYear && (
            <p className="text-gray-600">
              <span className="font-medium">Year:</span> {book.publicationYear}
            </p>
          )}
          {book.publisher && (
            <p className="text-gray-600">
              <span className="font-medium">Publisher:</span> {book.publisher}
            </p>
          )}
          {book.publicationPlace && (
            <p className="text-gray-600 text-xs">
              {book.publicationPlace}
            </p>
          )}
        </div>

        {book.description && (
          <p className="text-sm text-gray-500 mb-3 line-clamp-3 flex-1">
            {book.description}
          </p>
        )}

        <div className="mt-auto space-y-2 pt-3 border-t border-gray-100">
          {book.genre && (
            <div className="flex flex-wrap gap-1">
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                {book.genre}
              </span>
              {book.category && (
                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                  {book.category}
                </span>
              )}
            </div>
          )}
          
          {book.referenceNo && (
            <p className="text-xs text-gray-500 font-mono">
              Ref: {book.referenceNo}
            </p>
          )}
          
          {book.isbn && (
            <p className="text-xs text-gray-500">
              ISBN: {book.isbn}
            </p>
          )}

          {book.copies && (
            <p className="text-xs text-gray-600">
              <span className="font-medium">Copies:</span> {book.copies}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Enhanced Book Row for List View
const EnhancedBookRow: React.FC<{ book: GoogleSheetsBook; onClick: () => void }> = ({ book, onClick }) => {
  return (
    <div 
      className="bg-white rounded-lg border border-gray-200 hover:border-green-500 hover:shadow-lg transition-all duration-200 p-6 cursor-pointer"
      onClick={onClick}
    >
      <div className="flex gap-6">
        {/* Cover Image */}
        <div className="w-24 h-32 flex-shrink-0">
          {book.coverUrl ? (
            <img
              src={book.coverUrl}
              alt={`Cover of ${book.title}`}
              loading="lazy"
              className="w-full h-full object-cover rounded shadow-md"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-green-100 to-green-50 rounded shadow-md">
              <BookOpen className="w-8 h-8 text-green-600 opacity-40" />
            </div>
          )}
        </div>

        {/* Book Details */}
        <div className="flex-1 min-w-0">
          <h3 className="text-xl font-bold text-gray-900 mb-3">
            {book.title || 'Untitled'}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm mb-4">
            {book.author && (
              <div>
                <span className="font-semibold text-gray-700">Author:</span>
                <span className="text-gray-600 ml-2">{book.author}</span>
              </div>
            )}
            
            {book.publisher && (
              <div>
                <span className="font-semibold text-gray-700">Publisher:</span>
                <span className="text-gray-600 ml-2">{book.publisher}</span>
              </div>
            )}
            
            {book.publicationYear && (
              <div>
                <span className="font-semibold text-gray-700">Year:</span>
                <span className="text-gray-600 ml-2">{book.publicationYear}</span>
              </div>
            )}
            
            {book.publicationPlace && (
              <div>
                <span className="font-semibold text-gray-700">Place:</span>
                <span className="text-gray-600 ml-2">{book.publicationPlace}</span>
              </div>
            )}
            
            {book.genre && (
              <div>
                <span className="font-semibold text-gray-700">Genre:</span>
                <span className="text-gray-600 ml-2">{book.genre}</span>
              </div>
            )}
            
            {book.category && (
              <div>
                <span className="font-semibold text-gray-700">Category:</span>
                <span className="text-gray-600 ml-2">{book.category}</span>
              </div>
            )}
            
            {book.referenceNo && (
              <div>
                <span className="font-semibold text-gray-700">Reference No:</span>
                <span className="text-gray-600 ml-2 font-mono">{book.referenceNo}</span>
              </div>
            )}
            
            {book.copies && (
              <div>
                <span className="font-semibold text-gray-700">Copies Available:</span>
                <span className="text-gray-600 ml-2">{book.copies}</span>
              </div>
            )}
            
            {book.isbn && (
              <div>
                <span className="font-semibold text-gray-700">ISBN:</span>
                <span className="text-gray-600 ml-2">{book.isbn}</span>
              </div>
            )}
          </div>

          {book.description && (
            <p className="text-gray-600 text-sm mt-3 line-clamp-2">
              {book.description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const LibraryCataloguePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Parse initial state from URL params using helper
  const initialState = parseQueryParams(searchParams);
  
  const [searchQuery, setSearchQuery] = useState(initialState.searchQuery);
  const [filters, setFilters] = useState<SearchFilters>(initialState.filters);
  const [currentPage, setCurrentPage] = useState(initialState.page);
  const [pageSize, setPageSize] = useState(initialState.pageSize);
  const [viewMode, setViewMode] = useState<ViewMode>(initialState.viewMode);
  
  // Modal state for book details
  const [selectedBook, setSelectedBook] = useState<GoogleSheetsBook | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const handleBookClick = (book: GoogleSheetsBook) => {
    setSelectedBook(book);
    setIsModalOpen(true);
  };
  
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBook(null);
  };

  // Update URL params when search state changes using helper
  useEffect(() => {
    const params = buildQueryParams({
      searchQuery,
      filters,
      page: currentPage,
      pageSize,
      viewMode,
    });
    
    setSearchParams(params, { replace: true });
  }, [searchQuery, filters, currentPage, pageSize, viewMode, setSearchParams]);

  // Determine if we should show random sample or search results
  const showRandomSample = !searchQuery || searchQuery.trim() === '';

  // Fetch random sample when no search query
  const { data: randomSampleData, isLoading: isLoadingRandom } = useQuery({
    queryKey: ['library-random-sample'],
    queryFn: () => getRandomLibrarySample(12),
    enabled: showRandomSample,
    staleTime: 0, // Always fetch fresh random sample
  });

  // Fetch search results when query exists
  const { data: searchResultsData, isLoading: isLoadingSearch, isError, error } = useQuery({
    queryKey: ['library-search', searchQuery],
    queryFn: () => searchGoogleSheetsLibrary(searchQuery),
    enabled: !showRandomSample,
    staleTime: 60000, // Cache search results for 1 minute
  });

  // Determine which data to display
  const displayData = showRandomSample ? randomSampleData : searchResultsData;
  const isLoading = showRandomSample ? isLoadingRandom : isLoadingSearch;

  // For random sample, no pagination needed. For search results, apply pagination
  const filteredAndPaginatedData = useMemo(() => {
    if (!displayData) return null;

    // If showing random sample, return as-is (no pagination)
    if (showRandomSample) {
      return {
        books: displayData.results,
        total: displayData.results.length,
        page: 1,
        page_size: displayData.results.length,
      };
    }

    // If showing search results, apply pagination
    let filtered = displayData.results;

    // Apply additional filters if needed
    if (filters.author) {
      filtered = filtered.filter(book => 
        book.author?.toLowerCase().includes(filters.author!.toLowerCase())
      );
    }
    if (filters.subject) {
      filtered = filtered.filter(book => 
        book.genre?.toLowerCase().includes(filters.subject!.toLowerCase()) ||
        book.category?.toLowerCase().includes(filters.subject!.toLowerCase())
      );
    }

    const total = filtered.length;
    
    // Pagination for search results
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedBooks = filtered.slice(startIndex, endIndex);

    return {
      books: paginatedBooks,
      total: total,
      page: currentPage,
      page_size: pageSize,
    };
  }, [displayData, showRandomSample, filters, currentPage, pageSize]);

  const handleSearch = () => {
    setCurrentPage(1);
  };

  const handleFilterChange = (newFilters: SearchFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilters({});
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
  };

  const totalPages = filteredAndPaginatedData ? Math.ceil(filteredAndPaginatedData.total / filteredAndPaginatedData.page_size) : 0;
  const data = filteredAndPaginatedData; // Alias for compatibility with existing code

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Library & Publications', href: '/library' },
    { label: 'Catalogue' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-900 to-green-700 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-bold mb-3">Catalogue</h1>
            <p className="text-lg text-green-100">
              Search the Highland Institute holdings
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumbs */}
        <Breadcrumbs items={breadcrumbItems} />

        {/* Search Bar */}
        <div className="max-w-4xl mx-auto mb-8">
          <LibrarySearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            onSearch={handleSearch}
          />
        </div>

        {/* Layout: Filters + Results */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <LibraryFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onReset={handleResetFilters}
            />
          </aside>

          {/* Results */}
          <main className="flex-1">
            {/* Results Summary & Density Controls */}
            {data && (
              <div className="mb-6">
                <div 
                  className="text-gray-700 mb-4"
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {showRandomSample ? (
                    <>
                      <span className="font-medium">Showing {data.books.length} randomly selected books</span>
                      {' '}from our collection of <span className="font-medium">{randomSampleData?.total || 0}</span> items
                      <p className="text-sm text-gray-500 mt-1">Use the search bar above to find specific books</p>
                    </>
                  ) : (
                    <>
                      Showing <span className="font-medium">{data.books.length}</span> of{' '}
                      <span className="font-medium">{data.total}</span> search results
                    </>
                  )}
                </div>
                
                <ResultDensityControls
                  viewMode={viewMode}
                  pageSize={pageSize}
                  onViewModeChange={handleViewModeChange}
                  onPageSizeChange={handlePageSizeChange}
                />
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Searching catalogue...</p>
                </div>
              </div>
            )}

            {/* Error State */}
            {isError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-red-900 mb-2">Error Loading Results</h3>
                <p className="text-red-700 mb-4">
                  {error instanceof Error ? error.message : 'An error occurred while searching'}
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !isError && data && data.books.length === 0 && (
              <div className="text-center py-20">
                <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No results found</h3>
                <p className="text-gray-600 mb-6">
                  Try adjusting your search or filters to find what you're looking for.
                </p>
                <div className="space-y-2 text-sm text-gray-500 mb-6">
                  <p>• Broaden your search terms</p>
                  <p>• Check your spelling</p>
                  <p>• Remove some filters</p>
                </div>
                <div className="flex flex-col sm:flex-row justify-center gap-3">
                  {(searchQuery || Object.values(filters).some(v => v)) && (
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        handleResetFilters();
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      Clear All Filters
                    </button>
                  )}
                  <button
                    onClick={() => navigate('/library')}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Back to Library
                  </button>
                </div>
              </div>
            )}

            {/* Results - Grid or List */}
            {!isLoading && !isError && data && data.books.length > 0 && (
              <>
                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    {data.books.map((book, index) => (
                      <EnhancedBookCard 
                        key={book.referenceNo || index} 
                        book={book} 
                        onClick={() => handleBookClick(book)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4 mb-8">
                    {data.books.map((book, index) => (
                      <EnhancedBookRow 
                        key={book.referenceNo || index} 
                        book={book}
                        onClick={() => handleBookClick(book)}
                      />
                    ))}
                  </div>
                )}

                {/* Pagination - only show for search results, not random sample */}
                {!showRandomSample && totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    totalItems={data.total}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                  />
                )}
              </>
            )}
          </main>
        </div>
      </div>

      {/* Book Detail Modal */}
      {selectedBook && (
        <BookDetailModal
          book={selectedBook}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default LibraryCataloguePage;
